/**
 * R-82 — offline bug-report submit queue (IndexedDB via localStorage shim in tests).
 */
import type { BugReportSubmitInput } from '../../types/bugReport';

// B3 (non-blocking follow-up): this key is GLOBAL, not uid-scoped. On a shared
// browser, user B could flush user A's queued reports. Scope to
// `…:offline-queue:{uid}` (and migrate/clear on auth change) in a follow-up.
const STORE_KEY = 'datacrate:bug-report-offline-queue';

export interface QueuedBugReport {
  localId: string;
  payload: BugReportSubmitInput;
  queuedAt: string;
}

export interface BugReportQueueStore {
  getAll(): Promise<QueuedBugReport[]>;
  setAll(items: QueuedBugReport[]): Promise<void>;
}

const memoryStore: QueuedBugReport[] = [];

export const localBugReportQueueStore: BugReportQueueStore = {
  async getAll(): Promise<QueuedBugReport[]> {
    if (typeof localStorage === 'undefined') return [...memoryStore];
    try {
      const raw = localStorage.getItem(STORE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as QueuedBugReport[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  },
  async setAll(items: QueuedBugReport[]): Promise<void> {
    if (typeof localStorage === 'undefined') {
      memoryStore.length = 0;
      memoryStore.push(...items);
      return;
    }
    localStorage.setItem(STORE_KEY, JSON.stringify(items));
  },
};

let activeStore: BugReportQueueStore = localBugReportQueueStore;

export function setBugReportQueueStore(store: BugReportQueueStore): void {
  activeStore = store;
}

export function resetBugReportQueueStore(): void {
  activeStore = localBugReportQueueStore;
}

export async function enqueueOfflineBugReport(payload: BugReportSubmitInput): Promise<string> {
  const localId = `offline-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const items = await activeStore.getAll();
  items.push({ localId, payload, queuedAt: new Date().toISOString() });
  await activeStore.setAll(items);
  return localId;
}

export async function drainOfflineBugReportQueue(): Promise<QueuedBugReport[]> {
  const items = await activeStore.getAll();
  await activeStore.setAll([]);
  return items;
}

export async function peekOfflineBugReportQueue(): Promise<QueuedBugReport[]> {
  return activeStore.getAll();
}
